import { useEffect, useMemo, useState } from "react";

import { api, ArenaDetail, ArenaOption, Place } from "../api";
import { PlaceSearch } from "./PlaceSearch";

/**
 * Choosing the board.
 *
 * The count next to each option is the whole screen. "India · 659 places" and
 * "Kerala · 14 places" are two completely different games, and the hider is the
 * only person who gets to decide which one is being played. Showing the number
 * up front is what makes that a decision rather than an accident.
 */

const RADII = [100, 250, 500];

export function ArenaPicker({
  value,
  onPick,
}: {
  value: ArenaDetail | null;
  onPick: (arena: ArenaDetail) => void;
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ArenaOption[]>([]);
  const [regionsOf, setRegionsOf] = useState<ArenaOption | null>(null);
  /** Null while the country's regions are still loading. */
  const [regions, setRegions] = useState<ArenaOption[] | null>(null);
  const [near, setNear] = useState<Place | null>(null);
  const [radiusKm, setRadiusKm] = useState(250);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { arenas } = await api.arenas(query.trim() || undefined);
        if (live) setOptions(arenas);
      } catch {
        if (live) setError("Could not load the boards. Check your connection.");
      } finally {
        if (live) setLoading(false);
      }
    }, query ? 160 : 0);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);

  const openRegions = async (option: ArenaOption) => {
    setRegionsOf(option);
    setRegions(null);
    try {
      setRegions(await api.regions(option.countryCode!).then((r) => r.arenas));
    } catch {
      setRegions([]);
    }
  };

  const commit = async (token: string) => {
    setError(null);
    try {
      const { arena, playable } = await api.arena(token);
      if (!playable) {
        setError("Too few places on that board to make a round of it.");
        return;
      }
      onPick(arena);
    } catch {
      setError("Could not read that board.");
    }
  };

  const radiusToken = useMemo(
    () => (near ? `radius:${near.lat.toFixed(4)}:${near.lng.toFixed(4)}:${radiusKm}` : null),
    [near, radiusKm],
  );

  if (regionsOf) {
    return (
      <div>
        <button className="btn btn-ghost" onClick={() => setRegionsOf(null)}>
          ← All boards
        </button>
        <div className="label" style={{ margin: "16px 0 8px" }}>
          Anywhere in {regionsOf.label}, or narrow it down
        </div>
        <ArenaRow
          option={regionsOf}
          selected={value?.token === regionsOf.token}
          onPick={() => commit(regionsOf.token)}
        />
        {regions === null ? (
          <p className="clue-sub" style={{ marginTop: 12 }}>
            Reading the regions of {regionsOf.label}…
          </p>
        ) : regions.length === 0 ? (
          <p className="clue-sub" style={{ marginTop: 12 }}>
            No region in {regionsOf.label} has enough places to play on by itself.
          </p>
        ) : (
          regions.map((region) => (
            <ArenaRow
              key={region.token}
              option={region}
              selected={value?.token === region.token}
              onPick={() => commit(region.token)}
            />
          ))
        )}
        {error && (
          <p style={{ color: "var(--flare)", marginTop: 10 }} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <input
        value={query}
        placeholder="A country, a continent, a state…"
        aria-label="Search for a board"
        onChange={(e) => setQuery(e.target.value)}
        style={{ width: "100%" }}
      />

      <div className="arena-list">
        {loading && options.length === 0 && (
          <p className="clue-sub">Loading boards…</p>
        )}
        {options.map((option) => (
          <ArenaRow
            key={option.token}
            option={option}
            selected={value?.token === option.token}
            onPick={() => commit(option.token)}
            onNarrow={
              option.countryCode ? () => openRegions(option) : undefined
            }
          />
        ))}
        {!loading && options.length === 0 && (
          <p className="clue-sub">Nothing matches “{query}”.</p>
        )}
      </div>

      <div className="label" style={{ margin: "22px 0 8px" }}>
        Or draw a circle
      </div>
      <p className="clue-sub" style={{ marginTop: 0 }}>
        Somewhere within reach of a place you both know.
      </p>
      <PlaceSearch placeholder="Centre it on…" onPick={setNear} />
      {near && (
        <div style={{ marginTop: 10 }}>
          <div className="arena-chips">
            {RADII.map((km) => (
              <button
                key={km}
                className={`chip ${radiusKm === km ? "on" : ""}`}
                onClick={() => setRadiusKm(km)}
              >
                {km} km
              </button>
            ))}
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 10 }}
            onClick={() => radiusToken && commit(radiusToken)}
          >
            Within {radiusKm} km of {near.name}
          </button>
        </div>
      )}

      {error && (
        <p style={{ color: "var(--flare)", marginTop: 12 }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function ArenaRow({
  option,
  selected,
  onPick,
  onNarrow,
}: {
  option: ArenaOption;
  selected: boolean;
  onPick: () => void;
  onNarrow?: () => void;
}) {
  return (
    <div className={`arena-row ${selected ? "on" : ""}`}>
      <button className="arena-main" onClick={onPick}>
        <span className="arena-label">{option.label}</span>
        <span className="arena-count">
          {option.count.toLocaleString()} places
        </span>
      </button>
      {onNarrow && (
        <button className="arena-narrow" onClick={onNarrow} aria-label={`Regions in ${option.label}`}>
          Regions →
        </button>
      )}
    </div>
  );
}
